import React, { forwardRef } from 'react';
import { MenuItem, TextField, type TextFieldProps } from '@mui/material';
import { US_STATES } from '@shared/constants/states';

type StateSelectProps = Omit<TextFieldProps, 'select' | 'children'> & {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

/**
 * Reusable state dropdown component for US state selection
 * Uses 2-letter state codes as values
 * Shows abbreviations in the form for space efficiency
 * Shows full name in dropdown for clarity
 */
export const StateSelect = forwardRef<HTMLDivElement, StateSelectProps>(({
  value = '',
  placeholder = 'State',
  sx,
  ...props
}, ref) => {
  // Custom render to show only abbreviation when selected
  const renderValue = (selected: string) => {
    if (!selected) {
      return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>State</span>;
    }
    return selected;
  };

  return (
    <TextField
      {...props}
      ref={ref}
      select
      value={value}
      placeholder={placeholder}
      slotProps={{
        select: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
          renderValue: renderValue as any,
          displayEmpty: true,
        },
      }}
      sx={{
        width: '80px',
        minWidth: '80px',
        maxWidth: '80px',
        flex: '0 0 80px',
        '& .MuiInputBase-input': {
          padding: '3px 8px',
          fontSize: '13px',
          height: '21px', // Input height
        },
        '& .MuiOutlinedInput-root': {
          height: '27px', // Total height including border
        },
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...(typeof sx === 'object' && !Array.isArray(sx) ? sx : {}),
      }}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {Object.entries(US_STATES).map(([code, name]) => (
        <MenuItem key={code} value={code}>
          {code} - {name}
        </MenuItem>
      ))}
    </TextField>
  );
});

StateSelect.displayName = 'StateSelect';
